import React from 'react';
import { useObservable } from 'react-use-observable';

import { Button, Card, Empty, Pagination, Rate, Spin, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import MyIcon from 'components/Icon';
import Truncate from 'components/Truncate';
import CopyText from 'v2/components/Shared/CopyText';
import PaymentStatusTag from 'v2/components/Shared/PaymentStatusTag';
import PaymentTypeImg from 'v2/components/Shared/PaymentTypeImg';
import useWidth from 'v2/hooks/useWidth';
import { EPaymentMethod, type ShoppingDelivery } from 'v2/interfaces/shopping';
import shoppingService from 'v2/services/shopping/product';
import tableService from 'v2/services/table';

import ProductReview from '../ProductReview';

type ProductListProps = {
  handleReloadProducts: () => void;
  onClickRow: (shopping: ShoppingDelivery) => void;
};

type UnprocessedReview = {
  invoiceId: number;
  productId: number;
  rating: number;
  comment: string | null;
};

const ProductList = ({ handleReloadProducts, onClickRow }: ProductListProps) => {
  const width = useWidth();
  const isMobile = width === 'xs' || width === 'sm';

  const [isLoading] = useObservable(() => tableService.isLoading(), []);
  const [productWillBeReviewd, setProductWillBeReviewd] = React.useState<ShoppingDelivery | null>(null);
  const [openProductReview, setOpenProductReview] = React.useState(false);

  const [invoicesList] = useObservable(() => {
    return shoppingService.list();
  }, []);

  const [pagination] = useObservable(() => {
    return shoppingService.getPaginationResponse();
  }, []);

  function setProductRating(invoice: ShoppingDelivery) {
    setProductWillBeReviewd(invoice);
    setOpenProductReview(true);
  }

  function onChangePage(page: number, pageSize: number) {
    shoppingService.setPagination({ current_page: page, per_page: pageSize });
  }

  function getPaymentMethodName(invoice: ShoppingDelivery) {
    if (invoice.payment_method.id === EPaymentMethod.PIX_AUTOMATICO) return invoice.payment_method.method;
    if (invoice.payment_method.id) return invoice.payment_method.name;
    return 'Desconhecido';
  }

  function getProductRating(invoice: ShoppingDelivery) {
    const hasReview = typeof invoice.product.review?.rating === 'number';
    const isPaid = invoice.payment_status.alias === 'paid';

    const unprocessedReviews: UnprocessedReview[] = JSON.parse(localStorage.getItem('unprocessed-reviews')) || [];
    const unprocessedReview =
      unprocessedReviews.filter(
        review => review.invoiceId === invoice.invoice_id && review.productId === invoice.product.id
      )[0] || null;

    const productReviewedButNotProcessed = !hasReview && !!unprocessedReview;
    const ratingValue = productReviewedButNotProcessed ? unprocessedReview.rating : invoice.product.review?.rating;

    if (hasReview && !!unprocessedReview) {
      const newUnprocessedReviews = unprocessedReviews.filter(
        review => review.invoiceId !== invoice.invoice_id && review.productId !== invoice.product.id
      );
      localStorage.setItem('unprocessed-reviews', JSON.stringify(newUnprocessedReviews));
    }

    if (!isPaid) return null;

    if (!hasReview && !unprocessedReview) {
      return (
        <Button
          type='link'
          size='small'
          onClick={e => {
            e.stopPropagation();
            setProductRating(invoice);
          }}
        >
          Avaliar produto
        </Button>
      );
    }

    return <Rate value={ratingValue} disabled />;
  }

  const columns: ColumnsType<ShoppingDelivery> = [
    {
      title: 'Produto',
      key: 'product',
      render: (_, delivery) => (
        <div className='flex items-center'>
          <div
            className='border-outline-default h-10 w-10 min-w-10 border bg-cover bg-center'
            style={{ backgroundImage: `url(${delivery.product.image})` }}
          />
          <div className='text-content-body px-2'>
            <Truncate count={70} final='...' title={delivery.product.title}>
              {delivery.product.title}
            </Truncate>
            <span className='text-content-subtitle block text-sm font-normal'>
              contato:{' '}
              {delivery.producer.email ? (
                <CopyText value={delivery.producer.email}>
                  <a href={`mailto:${delivery.producer.email}`} className='text-inherit hover:underline'>
                    {delivery.producer.email}
                  </a>
                </CopyText>
              ) : (
                '-'
              )}
            </span>
          </div>
        </div>
      )
    },
    {
      title: 'Tipo',
      dataIndex: ['product', 'type'],
      key: 'type'
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, delivery) => (
        <div className='flex flex-wrap gap-1'>
          <PaymentStatusTag delivery={delivery} />
          {delivery.order?.status ? <Tag color='blue'>{delivery.order.status}</Tag> : null}
        </div>
      )
    },
    {
      title: 'Forma de pagamento',
      key: 'paymentMethod',
      render: (_, delivery) => (
        <div className='flex items-center gap-2'>
          <PaymentTypeImg delivery={delivery} />
          {getPaymentMethodName(delivery)}
        </div>
      )
    },
    {
      title: 'Avaliação',
      key: 'rating',
      render: (_, delivery) => getProductRating(delivery)
    },
    {
      key: 'actions',
      render: (_, delivery) => (
        <div className='[&_svg]:fill-content-subtitle flex items-center justify-end [&_svg]:w-3.5'>
          <Button type='text' size='small' onClick={() => onClickRow(delivery)} className='p-2'>
            <MyIcon id='btn-options-shopping-table-row' name='icon-product-options' />
          </Button>
        </div>
      )
    }
  ];

  const paginationConfig = pagination
    ? {
        current: pagination.current_page || 1,
        pageSize: pagination.per_page || 10,
        total: pagination.total_items || 0,
        pageSizeOptions: [10, 20, 50, 100],
        showSizeChanger: true,
        onChange: onChangePage,
        showTotal: (total: number, range: [number, number]) => `${range[0]}-${range[1]} de ${total}`,
        responsive: true,
        className: 'mt-4'
      }
    : false;

  const dataSource = invoicesList || [];

  function renderMobileCards() {
    if (isLoading) {
      return (
        <div className='flex justify-center py-8'>
          <Spin />
        </div>
      );
    }

    if (dataSource.length === 0) {
      return <Empty description='Nenhum registro encontrado.' />;
    }

    return (
      <div className='flex flex-col gap-3'>
        {dataSource.map(delivery => (
          <Card key={delivery.invoice_id} size='small' className='cursor-pointer' onClick={() => onClickRow(delivery)}>
            <div className='flex flex-col gap-3'>
              <div className='flex gap-3'>
                <div
                  className='border-outline-default h-12 w-12 min-w-12 rounded border bg-neutral-100 bg-cover bg-center'
                  style={{ backgroundImage: `url(${delivery.product.image})` }}
                />
                <div className='min-w-0 flex-1'>
                  <div className='text-content-title truncate font-medium' title={delivery.product.title}>
                    {delivery.product.title}
                  </div>
                  <div className='text-content-subtitle text-xs'>{delivery.product.type}</div>
                </div>
              </div>

              <div className='flex flex-wrap items-center gap-1.5'>
                <PaymentStatusTag delivery={delivery} />
                {delivery.order?.status ? <Tag color='blue'>{delivery.order.status}</Tag> : null}
              </div>

              <div className='text-content-body flex items-center justify-between text-sm'>
                <div className='flex items-center gap-1.5'>
                  <PaymentTypeImg delivery={delivery} />
                  <span className='truncate' title={getPaymentMethodName(delivery)}>
                    {getPaymentMethodName(delivery)}
                  </span>
                </div>
                <div className='flex items-center gap-1.5'>
                  <span className='text-content-subtitle text-xs'>Valor:</span>
                  <span className='font-medium'>
                    {delivery.total_value.toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: delivery.currency_code
                    })}
                  </span>
                </div>
              </div>

              <div className='w-fit' onClick={e => e.stopPropagation()}>
                {getProductRating(delivery)}
              </div>
            </div>
          </Card>
        ))}

        {paginationConfig && <Pagination {...paginationConfig} size='small' className='mt-2 flex justify-center' />}
      </div>
    );
  }

  return (
    <>
      {isMobile ? (
        renderMobileCards()
      ) : (
        <Table<ShoppingDelivery>
          id='products-table'
          columns={columns}
          dataSource={dataSource}
          rowKey='invoice_id'
          loading={isLoading}
          locale={{ emptyText: 'Nenhum registro encontrado.' }}
          pagination={paginationConfig}
        />
      )}

      {productWillBeReviewd && (
        <ProductReview
          invoice={productWillBeReviewd}
          product={productWillBeReviewd.product}
          open={openProductReview}
          onClose={() => setOpenProductReview(false)}
          handleReloadProducts={handleReloadProducts}
        />
      )}
    </>
  );
};

export default ProductList;
